import { AdminApiRoutes } from '@gr/core/admin-api-routes';
import { ApiRoutes } from '@gr/core/api-routes';
import type { AdminIssueApiKeyRequestDto, ApiKeyDto, IssueApiKeyRequestDto, IssuedApiKeyDto } from '@gr/core/accounts/identity/api-keys';
import type {
  CreateUserRequestDto,
  LoginResponseDto,
  MeDto,
  RegisterRequestDto,
  UpdatePreferencesRequestDto,
  UpdateUserRequestDto,
  UserDto,
  UserListQuery,
} from '@gr/core/accounts/identity/dto';
import type { OperationResult } from '@gr/utils/operation-result';
import type { HttpClient } from '../../../shared/http/http-client';

export class IdentityApi {
  constructor(private readonly http: HttpClient) {}

  login(login: string, password: string): Promise<OperationResult<LoginResponseDto>> {
    return this.http.post(ApiRoutes.auth.login, { login, password });
  }
  // IAM-UC-14
  register(input: RegisterRequestDto): Promise<OperationResult<LoginResponseDto>> {
    return this.http.post(ApiRoutes.auth.register, input);
  }
  logout(): Promise<OperationResult<null>> {
    return this.http.post(ApiRoutes.auth.logout);
  }
  me(): Promise<OperationResult<MeDto>> {
    return this.http.get(ApiRoutes.auth.me);
  }
  changePassword(currentPassword: string, newPassword: string): Promise<OperationResult<null>> {
    return this.http.put(ApiRoutes.auth.password, { currentPassword, newPassword });
  }
  updatePreferences(input: UpdatePreferencesRequestDto): Promise<OperationResult<MeDto>> {
    return this.http.put(ApiRoutes.auth.preferences, input);
  }
  listUsers(): Promise<OperationResult<UserDto[]>> {
    return this.http.get(AdminApiRoutes.users.list);
  }
  /** IAM-UC-6.CC-2: поиск пользователя для автокомплита. */
  searchUsers(query: UserListQuery, signal?: AbortSignal): Promise<OperationResult<UserDto[]>> {
    return this.http.get(AdminApiRoutes.users.list, { ...query }, signal);
  }
  // IAM-UC-9, IAM-UC-10
  myApiKeys(): Promise<OperationResult<ApiKeyDto[]>> {
    return this.http.get(ApiRoutes.apiKeys.mine);
  }
  issueMyApiKey(input: IssueApiKeyRequestDto): Promise<OperationResult<IssuedApiKeyDto>> {
    return this.http.post(ApiRoutes.apiKeys.mine, input);
  }
  revokeMyApiKey(keyId: string): Promise<OperationResult<ApiKeyDto>> {
    return this.http.post(ApiRoutes.apiKeys.revokeMine(keyId));
  }
  // IAM-UC-13
  deleteMyApiKey(keyId: string): Promise<OperationResult<null>> {
    return this.http.delete(ApiRoutes.apiKeys.mineById(keyId));
  }
  // IAM-UC-11
  allApiKeys(): Promise<OperationResult<ApiKeyDto[]>> {
    return this.http.get(AdminApiRoutes.apiKeys.list);
  }
  issueApiKey(input: AdminIssueApiKeyRequestDto): Promise<OperationResult<IssuedApiKeyDto>> {
    return this.http.post(AdminApiRoutes.apiKeys.list, input);
  }
  revokeApiKey(keyId: string): Promise<OperationResult<ApiKeyDto>> {
    return this.http.post(AdminApiRoutes.apiKeys.revoke(keyId));
  }
  // IAM-UC-13
  deleteApiKey(keyId: string): Promise<OperationResult<null>> {
    return this.http.delete(AdminApiRoutes.apiKeys.byId(keyId));
  }
  createUser(input: CreateUserRequestDto): Promise<OperationResult<UserDto>> {
    return this.http.post(AdminApiRoutes.users.list, input);
  }
  updateUser(userId: string, input: UpdateUserRequestDto): Promise<OperationResult<UserDto>> {
    return this.http.patch(AdminApiRoutes.users.byId(userId), input);
  }
  resetPassword(userId: string, password: string): Promise<OperationResult<null>> {
    return this.http.post(AdminApiRoutes.users.password(userId), { password });
  }
}
